import { Modal, Box, Button, Divider, Typography, Chip } from '@mui/material';
import { t } from 'i18next';
import { OTPInput } from '@components';
import { HttpErrorMessage } from '@views/pages/Auth/common';
import { QRCode } from 'react-qr-code';
import { postRequest } from '@config/axios/axios.config';
import { paths } from '@common/types';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useQueryClient } from '@tanstack/react-query';

interface ISetupAppMFAModalProps {
  onClose: () => void;
  isOpen: boolean;
}

export const SetupAppMFAModal = ({
  onClose,
  isOpen,
}: ISetupAppMFAModalProps) => {
  const [otpValue, setOTPValue] = useState<string>('');
  const [hasError, setHasError] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [mfaLink, setMFALink] = useState<string>('');
  const [mfaManualCode, setMFAManualCode] = useState<string>('');

  const queryClient = useQueryClient();

  const handleOnChangeOtpValue = (newValue: string) => {
    setOTPValue(newValue);
  };

  const registerMFARequest = async () => {
    const response = await postRequest<
      paths['/profile/new-mfa-method']['post']['requestBody']['content']['application/json'],
      paths['/profile/new-mfa-method']['post']['responses']['201']['content']['application/json']
    >('/profile/new-mfa-method', {
      type: 'app',
    });

    if (response.data.url && response.data.secret) {
      setMFALink(response.data.url);
      setMFAManualCode(response.data.secret);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setOTPValue('');
      setHasError(false);
      registerMFARequest();
    }
  }, [isOpen]);

  useEffect(() => {
    async function fetchMyAPI() {
      if (isSubmitting) return;

      try {
        setIsSubmitting(true);
        setHasError(false);

        await postRequest<
          paths['/profile/new-mfa-method/confirm']['post']['requestBody']['content']['application/json'],
          paths['/profile/new-mfa-method/confirm']['post']['responses']['200']['content']['application/json']
        >('/profile/new-mfa-method/confirm', {
          type: 'app',
          secret: otpValue,
        });

        queryClient.invalidateQueries({ queryKey: ['profile'] });

        toast.success('MFA setup successful');
        onClose();
      } catch {
        setHasError(true);
        setOTPValue('');
      } finally {
        setIsSubmitting(false);
      }
    }

    if (otpValue.length === 6 && !otpValue.includes(' ')) {
      fetchMyAPI();
    }
  }, [otpValue]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 1,
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '0.5rem',
            gap: '16px',
            width: '100%',
            borderRadius: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              padding: 0,
            }}
          >
            <VerifiedUserIcon sx={{ fontSize: '3rem' }} color='primary' />
            <Typography
              sx={{ fontWeight: '600', fontSize: '1.5rem', color: 'primary' }}
            >
              Protect your account with MFA
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ paddingBottom: '16px' }}>
              Scan QR code:
            </Typography>
            <QRCode
              size={256}
              style={{ height: 'auto', maxWidth: '40%', width: '40%' }}
              value={mfaLink}
              viewBox={`0 0 64 64`}
            />
            {mfaManualCode && (
              <>
                <Chip
                  label={mfaManualCode}
                  sx={{ borderRadius: 'inherit', mt: '2rem' }}
                />
              </>
            )}
          </Box>

          <Divider sx={{ margin: '0.3rem', width: '100%' }} />

          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 'regular',
              textAlign: 'center',
            }}
          >
            Enter the one-time password shown in authentication app to verify
            your account.
          </Typography>

          {hasError && (
            <HttpErrorMessage
              message={
                'The MFA code you entered is invalid. Please check your authenticator app or SMS and try again.'
              }
            />
          )}

          <OTPInput
            value={otpValue}
            onChange={handleOnChangeOtpValue}
            length={6}
          />
        </Box>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} sx={{ mr: 1 }}>
            {t('general.cancel')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
